import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { SignIn, SignUp, useAuth } from '@clerk/clerk-react';
import { ReactComponent as LogoMark } from './assets/images/logo-mark.svg';
import Dashboard from './pages/Dashboard';
import Sessions from './pages/Sessions';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import Billing from './pages/Billing';
import ApiKeys from './pages/ApiKeys';
import Chat from './pages/Chat';
import Usage from './pages/Usage';
import Home from './pages/Home';
import Waitlist from './pages/Waitlist';
import Features from './pages/Features';
import HowItWorks from './pages/HowItWorks';
import Pricing from './pages/Pricing';
import Researchers from './pages/Researchers';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Cookies from './pages/Cookies';
import Layout from './components/Layout';
import CookieBanner from './components/CookieBanner';
import { ThemeProvider } from './context/ThemeContext';

function ProtectedRoute({ children }) {
  const { isSignedIn, isLoaded } = useAuth();
  if (!isLoaded) return <div style={s.loading}>Loading...</div>;
  if (!isSignedIn) return <Navigate to="/sign-in" replace />;
  return children;
}

const clerkAppearance = {
  variables: {
    colorPrimary: '#00FFFF',
    colorBackground: '#111C6E',
    colorText: '#F8F9FA',
    colorTextSecondary: 'rgba(248,249,250,0.72)',
    colorInputBackground: 'rgba(255,255,255,0.07)',
    colorInputText: '#F8F9FA',
    colorNeutral: '#F8F9FA',
    borderRadius: '12px',
  },
  elements: {
    card: {
      background: '#111C6E',
      border: '1px solid rgba(0,255,255,0.30)',
      boxShadow: '0 0 0 1px rgba(0,255,255,0.08), 0 24px 64px rgba(0,0,0,0.65), 0 0 80px rgba(0,255,255,0.06)',
    },
    headerTitle: { color: '#F8F9FA', fontFamily: "'Satoshi','Inter',sans-serif", fontWeight: 700 },
    headerSubtitle: { color: 'rgba(248,249,250,0.72)' },
    formButtonPrimary: { background: '#FF5722', fontFamily: "'Satoshi','Inter',sans-serif", fontWeight: 700 },
    footerActionLink: { color: '#00FFFF' },
    dividerLine: { background: 'rgba(0,255,255,0.12)' },
    formFieldInput: { border: '1px solid rgba(0,255,255,0.18)' },
    socialButtonsBlockButton: {
      background: 'rgba(255,255,255,0.06)',
      border: '1px solid rgba(255,255,255,0.12)',
    },
    rootBox: { background: 'transparent', boxShadow: 'none' },
  },
};

export default function App() {
  return (
    <ThemeProvider>
    <BrowserRouter>
      <CookieBanner />
      <Routes>
        <Route path="/sign-in/*" element={
          <div style={s.authPage}>
            <div style={s.authLogo}>
              <LogoMark width="28" height="28" style={{verticalAlign:'middle',marginRight:8}} />
              SenseCritiq
            </div>
            <div style={s.authGlow}>
              <SignIn routing="path" path="/sign-in" forceRedirectUrl="/dashboard" appearance={clerkAppearance} />
            </div>
          </div>
        } />
        <Route path="/sign-up/*" element={
          <div style={s.authPage}>
            <div style={s.authLogo}>
              <LogoMark width="28" height="28" style={{verticalAlign:'middle',marginRight:8}} />
              SenseCritiq
            </div>
            <div style={s.authGlow}>
              <SignUp routing="path" path="/sign-up" forceRedirectUrl="/dashboard" appearance={clerkAppearance} />
            </div>
          </div>
        } />
        <Route path="/"              element={<Home />} />
        <Route path="/waitlist"      element={<Waitlist />} />
        <Route path="/privacy"       element={<Privacy />} />
        <Route path="/terms"         element={<Terms />} />
        <Route path="/cookies"       element={<Cookies />} />
        <Route path="/how-it-works"  element={<HowItWorks />} />
        <Route path="/features"      element={<Features />} />
        <Route path="/researchers"   element={<Researchers />} />
        <Route path="/pricing"       element={<Pricing />} />
        <Route path="/dashboard" element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />
        <Route path="/sessions"  element={<ProtectedRoute><Layout><Sessions /></Layout></ProtectedRoute>} />
        <Route path="/projects"     element={<ProtectedRoute><Layout><Projects /></Layout></ProtectedRoute>} />
        <Route path="/projects/:id" element={<ProtectedRoute><Layout><ProjectDetail /></Layout></ProtectedRoute>} />
        <Route path="/billing"   element={<ProtectedRoute><Layout><Billing /></Layout></ProtectedRoute>} />
        <Route path="/keys"      element={<ProtectedRoute><Layout><ApiKeys /></Layout></ProtectedRoute>} />
        <Route path="/usage"     element={<ProtectedRoute><Layout><Usage /></Layout></ProtectedRoute>} />
        <Route path="/chat"      element={<ProtectedRoute><Layout fullHeight><Chat /></Layout></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
    </ThemeProvider>
  );
}

const s = {
  authPage: {
    minHeight: '100vh', display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    background: '#030A3C',
    backgroundImage: [
      'radial-gradient(ellipse 70% 50% at 50% -10%, rgba(0,255,255,0.12) 0%, transparent 65%)',
      'radial-gradient(ellipse 40% 30% at 50% 100%, rgba(0,0,80,0.6) 0%, transparent 70%)',
    ].join(', '),
  },
  authLogo: {
    fontFamily: "'Satoshi','Inter',sans-serif", fontSize: 26, fontWeight: 700,
    letterSpacing: '-0.02em', color: '#F8F9FA', marginBottom: 32,
    display: 'flex', alignItems: 'center',
  },
  authGlow: {},
  loading: {
    minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: '#030A3C', color: 'rgba(248,249,250,0.65)', fontSize: 14,
    fontFamily: "'Inter',sans-serif",
  },
};
